<script setup lang="ts">
import KpSelectField from '@/components/KpSelectField.vue';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import KpNutzungsartSelect from '@/components/KpNutzungsartSelect.vue';
import type { Nutzungsart } from '@/models/Nutzungsart';
import KpCheckboxField from '@/components/KpCheckboxField.vue';

const t = useI18n().t;
const emit = defineEmits(['next', 'prev']);

const props = defineProps<{
    typ: string | null;
    kanton_id: number | null;
    jahr: number | null;
}>();

const data: Ref<GebaeudeAuswertungsDaten> = ref({
    systemgrenze: null,
    auswertungsUmfang: null,
    klimakorrektur: null,
    methodikKlimakorrektur: null,
    aggregation: null,
    nutzungsartFilter: null,
    grossverbraucher_beruecksichtigen: true,
    angrenzende_gebaeude_beruecksichtigen: true,
});

enum Systemgrenze {
    gesamtenergie = 'gesamtenergie',
    endenergie = 'endenergie',
}

enum AuswertungsUmfang {
    alle_gebaeude = 'alle_gebaeude',
    wohn_und_dienstleistungsgebaeude = 'wohn_und_dienstleistungsgebaeude',
    wohngebaeude = 'wohngebaeude',
}

enum MethodikKlimakorrektur {
    hgt = 'hgt',
    atd = 'atd',
}

enum Aggregation {
    gemeinde = 'gemeinde',
    nutzungsart = 'nutzungsart',
    bauperiode = 'bauperiode',
}

enum AggregationShort {
    gemeinde = 'gemeinde',
    bauperiode = 'bauperiode',
}

enum Klimakorrektur {
    ja = 'ja',
    nein = 'nein',
}

export type GebaeudeAuswertungsDaten = {
    systemgrenze: Systemgrenze | null;
    auswertungsUmfang: AuswertungsUmfang | null;
    klimakorrektur: Klimakorrektur | null;
    methodikKlimakorrektur: MethodikKlimakorrektur | null;
    aggregation: Aggregation | AggregationShort | null;
    nutzungsartFilter: Nutzungsart | null;
    grossverbraucher_beruecksichtigen: boolean;
    angrenzende_gebaeude_beruecksichtigen: boolean;
};

const auswertungsUmfangOptions = computed(() => {
    return Object.entries(AuswertungsUmfang)
        .map(([id, value]) => ({ id, value: t(value) }))
        .filter((value) => {
            if (data?.value.systemgrenze === Systemgrenze.gesamtenergie) {
                return value.id === AuswertungsUmfang.alle_gebaeude;
            }
            return true;
        });
});

function prev() {
    cleanEntries();
    emit('prev');
}

function next() {
    emit('next', data.value);
    cleanEntries();
}

function cleanEntries() {
    data.value.systemgrenze = null;
    data.value.auswertungsUmfang = null;
    data.value.klimakorrektur = null;
    data.value.methodikKlimakorrektur = null;
    data.value.aggregation = null;
    data.value.nutzungsartFilter = null;
    data.value.grossverbraucher_beruecksichtigen = true;
    data.value.angrenzende_gebaeude_beruecksichtigen = true;
}

const nextEnabled = computed(() => {
    if (props.typ === 'energieverbrauch' || props.typ === 'co2_emissionen') {
        return (
            data.value.systemgrenze !== null &&
            data.value.auswertungsUmfang !== null &&
            (data.value.klimakorrektur === Klimakorrektur.nein ||
                (data.value.klimakorrektur === Klimakorrektur.ja && data.value.methodikKlimakorrektur !== null))
        );
    } else if (
        props.typ === 'anzahl_heizsysteme' ||
        props.typ === 'anzahl_warmwassersysteme' ||
        props.typ === 'anzahl_gebaeude'
    ) {
        return data.value.aggregation !== null;
    } else {
        return true;
    }
});
</script>

<template>
    <div class="formgrid grid align-items-start">
        <template v-if="typ === 'energieverbrauch' || typ === 'co2_emissionen'">
            <KpSelectField
                class="col-6"
                fluid
                :label="$t('systemgrenze')"
                v-model="data.systemgrenze"
                :options="Object.entries(Systemgrenze).map(([id, value]) => ({ id, value: $t(value) }))"
                option-label="value"
                option-value="id"
            ></KpSelectField>
            <KpSelectField
                class="col-6"
                fluid
                :label="$t('auswertung_fuer')"
                v-model="data.auswertungsUmfang"
                :options="auswertungsUmfangOptions"
                option-label="value"
                option-value="id"
            ></KpSelectField>
            <KpSelectField
                class="col-6"
                fluid
                :label="$t('klimakorrektur')"
                v-model="data.klimakorrektur"
                :options="Object.entries(Klimakorrektur).map(([id, value]) => ({ id, value: $t(value) }))"
                option-label="value"
                option-value="id"
            ></KpSelectField>
            <KpSelectField
                v-if="data.klimakorrektur && data.klimakorrektur === Klimakorrektur.ja"
                class="col-6"
                fluid
                :label="$t('methodik_klimakorrektur')"
                v-model="data.methodikKlimakorrektur"
                :options="Object.entries(MethodikKlimakorrektur).map(([id, value]) => ({ id, value: $t(value) }))"
                option-label="value"
                option-value="id"
            ></KpSelectField>
        </template>
        <template v-else-if="typ === 'anzahl_heizsysteme' || typ === 'anzahl_warmwassersysteme'">
            <KpNutzungsartSelect
                showClear
                class="col-6"
                fluid
                :label="$t('nutzungsart')"
                :placeholder="$t('alle_nutzungsarten')"
                v-model="data.nutzungsartFilter"
            ></KpNutzungsartSelect>
            <KpSelectField
                class="col-6"
                fluid
                :label="$t('aggregation')"
                v-model="data.aggregation"
                :options="Object.entries(Aggregation).map(([id, value]) => ({ id, value: $t(value) }))"
                option-label="value"
                option-value="id"
            ></KpSelectField>
        </template>
        <template v-else-if="typ === 'anzahl_gebaeude'">
            <KpSelectField
                class="col-6"
                fluid
                :label="$t('aggregation')"
                v-model="data.aggregation"
                :options="Object.entries(AggregationShort).map(([id, value]) => ({ id, value: $t(value) }))"
                option-label="value"
                option-value="id"
            ></KpSelectField>
        </template>
        <template v-else></template>
    </div>
    <div class="formgrid grid align-items-start">
        <KpCheckboxField
            class="col-12"
            :label="$t('angrenzende_gebaeude_beruecksichtigen')"
            binary
            v-model="data.angrenzende_gebaeude_beruecksichtigen"
        />
        <KpCheckboxField
            class="col-12"
            :label="$t('grossverbraucher_beruecksichtigen')"
            binary
            v-model="data.grossverbraucher_beruecksichtigen"
        />
    </div>
    <div class="flex pt-6 justify-content-between">
        <Button :label="$t('zurueck')" severity="secondary" icon="fa-solid fa-arrow-left" @click="prev()"></Button>
        <Button
            :label="$t('weiter')"
            :disabled="!nextEnabled"
            icon="fa-solid fa-arrow-right"
            iconPos="right"
            @click="() => next()"
        ></Button>
    </div>
</template>
